import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  InputLabel,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
  Grid,
} from "@mui/material";
import {
  Save as SaveIcon,
  Send as SendIcon,
  Info as InfoIcon,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import {
  EmailProvider,
  EmailSetupFormData,
  EmailValidationErrors,
} from "./types";
import emailService from "./services/emailService";

const EmailSetupPage: React.FC = () => {
  const [formData, setFormData] = useState<EmailSetupFormData>({
    provider: "smtp",
    host: "",
    port: 587,
    secure: true,
    username: "",
    password: "",
    fromAddress: "",
    fromName: "",
    testRecipient: "",
  });

  const [errors, setErrors] = useState<EmailValidationErrors>({});
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);

  // Load existing configuration on mount
  useEffect(() => {
    console.log(
      "🔄 EmailSetupPage: Component mounted, loading configuration..."
    );
    loadConfiguration();
  }, []);

  const loadConfiguration = async () => {
    console.log(
      "📥 loadConfiguration: Starting to load email configuration..."
    );
    try {
      const config = await emailService.getEmailConfiguration();
      console.log("✅ loadConfiguration: Configuration received:", {
        hasConfig: !!config,
        provider: config?.provider,
        host: config?.host,
        port: config?.port,
        secure: config?.secure,
        username: config?.username,
        hasPassword: !!config?.password,
        fromAddress: config?.fromAddress,
        fromName: config?.fromName,
      });

      if (config) {
        const newFormData = {
          provider: config.provider,
          host: config.host,
          port: config.port,
          secure: config.secure,
          username: config.username,
          password: "", // Don't load password for security
          fromAddress: config.fromAddress,
          fromName: config.fromName || "",
          testRecipient: "",
        };

        console.log("📝 loadConfiguration: Setting form data:", newFormData);
        setFormData(newFormData);
        setConfigLoaded(true);
        console.log(
          "✅ loadConfiguration: Configuration loaded successfully, configLoaded set to true"
        );
      } else {
        console.log("ℹ️ loadConfiguration: No existing configuration found");
      }
    } catch (error) {
      console.error("❌ loadConfiguration: Error loading configuration:", {
        error,
        message: error instanceof Error ? error.message : "Unknown error",
        stack: error instanceof Error ? error.stack : undefined,
      });
    }
  };

  const handleInputChange = (field: keyof EmailSetupFormData, value: any) => {
    console.log("✏️ handleInputChange:", {
      field,
      value,
      valueType: typeof value,
      previousValue: formData[field],
    });

    setFormData((prev) => {
      const newFormData = {
        ...prev,
        [field]: value,
      };
      console.log("📝 handleInputChange: Updated form data:", newFormData);
      return newFormData;
    });

    // Clear error for this field
    setErrors((prev) => {
      const newErrors = {
        ...prev,
        [field]: undefined,
      };
      console.log("🧹 handleInputChange: Cleared error for field:", field);
      return newErrors;
    });

    // Clear messages
    setSuccessMessage("");
    setErrorMessage("");
    console.log("🧹 handleInputChange: Cleared success and error messages");
  };

  const validateForm = (): boolean => {
    console.log("🔍 validateForm: Starting form validation...");
    console.log("📋 validateForm: Current form data:", {
      host: formData.host,
      port: formData.port,
      username: formData.username,
      hasPassword: !!formData.password,
      fromAddress: formData.fromAddress,
      testRecipient: formData.testRecipient,
      configLoaded,
    });

    const newErrors: EmailValidationErrors = {};

    if (!formData.host.trim()) {
      newErrors.host = "SMTP host is required";
      console.log("❌ validateForm: Host validation failed - empty host");
    }

    if (!formData.port || formData.port < 1 || formData.port > 65535) {
      newErrors.port = "Port must be between 1 and 65535";
      console.log("❌ validateForm: Port validation failed:", formData.port);
    }

    if (!formData.username.trim()) {
      newErrors.username = "Username is required";
      console.log(
        "❌ validateForm: Username validation failed - empty username"
      );
    }

    if (!formData.password.trim() && !configLoaded) {
      newErrors.password = "Password is required";
      console.log(
        "❌ validateForm: Password validation failed - empty password (new configuration)"
      );
    } else if (formData.password.trim()) {
      console.log("✅ validateForm: Password provided");
    } else {
      console.log(
        "ℹ️ validateForm: Password not required (existing configuration)"
      );
    }

    if (!formData.fromAddress.trim()) {
      newErrors.fromAddress = "From address is required";
      console.log(
        "❌ validateForm: From address validation failed - empty address"
      );
    } else if (!emailService.validateEmailAddress(formData.fromAddress)) {
      newErrors.fromAddress = "Invalid email address format";
      console.log(
        "❌ validateForm: From address validation failed - invalid format:",
        formData.fromAddress
      );
    } else {
      console.log("✅ validateForm: From address valid:", formData.fromAddress);
    }

    if (
      formData.testRecipient &&
      !emailService.validateEmailAddress(formData.testRecipient)
    ) {
      newErrors.testRecipient = "Invalid email address format";
      console.log(
        "❌ validateForm: Test recipient validation failed - invalid format:",
        formData.testRecipient
      );
    } else if (formData.testRecipient) {
      console.log(
        "✅ validateForm: Test recipient valid:",
        formData.testRecipient
      );
    }

    const isValid = Object.keys(newErrors).length === 0;
    console.log("🔍 validateForm: Validation result:", {
      isValid,
      errorCount: Object.keys(newErrors).length,
      errors: newErrors,
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSave = async () => {
    console.log("💾 handleSave: Save button clicked");
    console.log(
      "📋 handleSave: Current form data:",
      JSON.stringify(formData, null, 2)
    );

    if (!validateForm()) {
      console.log("❌ handleSave: Form validation failed, aborting save");
      return;
    }

    console.log("✅ handleSave: Form validation passed, proceeding with save");
    setLoading(true);
    setSuccessMessage("");
    setErrorMessage("");

    try {
      const saveData = {
        provider: formData.provider,
        host: formData.host,
        port: formData.port,
        secure: formData.secure,
        username: formData.username,
        fromAddress: formData.fromAddress,
        fromName: formData.fromName,
        passwordProvided: !!formData.password,
      };

      console.log(
        "📤 handleSave: Sending configuration to emailService.saveEmailConfiguration:",
        saveData
      );
      console.log("🔐 handleSave: Password length:", formData.password.length);

      const startTime = Date.now();
      const result = await emailService.saveEmailConfiguration(formData);
      const endTime = Date.now();

      console.log(
        "⏱️ handleSave: Save request completed in",
        endTime - startTime,
        "ms"
      );
      console.log("📥 handleSave: Save result:", {
        success: result.success,
        message: result.message,
      });

      if (result.success) {
        console.log("✅ handleSave: Configuration saved successfully");
        setSuccessMessage(result.message);
        setConfigLoaded(true);
        console.log("🔄 handleSave: Set configLoaded to true");
      } else {
        console.log("❌ handleSave: Save failed:", result.message);
        setErrorMessage(result.message);
      }
    } catch (error: any) {
      console.error("❌ handleSave: Exception occurred during save:", {
        error,
        message: error.message,
        stack: error.stack,
        response: error.response,
        responseData: error.response?.data,
        responseStatus: error.response?.status,
      });
      setErrorMessage(error.message || "Failed to save configuration");
    } finally {
      setLoading(false);
      console.log("🔄 handleSave: Loading state set to false");
    }
  };

  const handleTestConnection = async () => {
    console.log("🧪 handleTestConnection: Test connection button clicked");
    console.log(
      "📋 handleTestConnection: Current form data:",
      JSON.stringify(formData, null, 2)
    );

    if (!validateForm()) {
      console.log(
        "❌ handleTestConnection: Form validation failed, aborting test"
      );
      return;
    }

    if (!formData.testRecipient) {
      console.log("❌ handleTestConnection: No test recipient provided");
      setErrors((prev) => ({
        ...prev,
        testRecipient: "Test recipient email is required for testing",
      }));
      return;
    }

    console.log("✅ handleTestConnection: Validation passed, starting test");
    setTestLoading(true);
    setSuccessMessage("");
    setErrorMessage("");

    try {
      console.log(
        "📤 handleTestConnection: Calling emailService.testEmailConfiguration"
      );
      console.log(
        "📧 handleTestConnection: Test recipient:",
        formData.testRecipient
      );

      const startTime = Date.now();
      const result = await emailService.testEmailConfiguration(
        formData,
        formData.testRecipient
      );
      const endTime = Date.now();

      console.log(
        "⏱️ handleTestConnection: Test completed in",
        endTime - startTime,
        "ms"
      );
      console.log("📥 handleTestConnection: Test result:", {
        success: result.success,
        message: result.message,
        error: result.error,
        details: result.details,
      });

      if (result.success) {
        console.log("✅ handleTestConnection: Test successful");
        setSuccessMessage(result.message);
      } else {
        console.log("❌ handleTestConnection: Test failed:", result.error);
        setErrorMessage(result.error || "Test failed");
      }
    } catch (error: any) {
      console.error(
        "❌ handleTestConnection: Exception occurred during test:",
        {
          error,
          message: error.message,
          stack: error.stack,
          response: error.response,
          responseData: error.response?.data,
          responseStatus: error.response?.status,
        }
      );
      setErrorMessage(error.message || "Failed to test connection");
    } finally {
      setTestLoading(false);
      console.log("🔄 handleTestConnection: Test loading state set to false");
    }
  };

  const providerPresets: Record<EmailProvider, Partial<EmailSetupFormData>> = {
    smtp: { port: 587, secure: true },
    gmail: { host: "smtp.gmail.com", port: 587, secure: true },
    outlook: { host: "smtp-mail.outlook.com", port: 587, secure: true },
    office365: { host: "smtp.office365.com", port: 587, secure: true },
    sendgrid: { host: "smtp.sendgrid.net", port: 587, secure: true },
    mailgun: { host: "smtp.mailgun.org", port: 587, secure: true },
    ses: {
      host: "email-smtp.us-east-1.amazonaws.com",
      port: 587,
      secure: true,
    },
  };

  const handleProviderChange = (provider: EmailProvider) => {
    console.log("🔄 handleProviderChange: Provider changed to:", provider);
    const preset = providerPresets[provider];
    console.log("📋 handleProviderChange: Applying preset:", preset);

    setFormData((prev) => {
      const newFormData = {
        ...prev,
        provider,
        ...preset,
      };
      console.log("📝 handleProviderChange: New form data:", newFormData);
      return newFormData;
    });
  };

  console.log("🔄 EmailSetupPage: Render cycle", {
    loading,
    testLoading,
    configLoaded,
    hasErrors: Object.keys(errors).length > 0,
    errorCount: Object.keys(errors).length,
    hasSuccessMessage: !!successMessage,
    hasErrorMessage: !!errorMessage,
    provider: formData.provider,
    host: formData.host,
  });

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 4, maxWidth: 800, mx: "auto" }}>
        <Typography variant="h4" gutterBottom>
          Email Configuration Setup
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Configure your email settings to enable email notifications and
          communications.
        </Typography>

        {successMessage && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => {
              console.log("🧹 Alert: Clearing success message");
              setSuccessMessage("");
            }}
          >
            {successMessage}
          </Alert>
        )}

        {errorMessage && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => {
              console.log("🧹 Alert: Clearing error message");
              setErrorMessage("");
            }}
          >
            {errorMessage}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Provider Selection */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Email Provider</InputLabel>
              <Select
                value={formData.provider}
                label="Email Provider"
                onChange={(e) =>
                  handleProviderChange(e.target.value as EmailProvider)
                }
              >
                <MenuItem value="smtp">Custom SMTP</MenuItem>
                <MenuItem value="gmail">Gmail</MenuItem>
                <MenuItem value="outlook">Outlook</MenuItem>
                <MenuItem value="office365">Office 365</MenuItem>
                <MenuItem value="sendgrid">SendGrid</MenuItem>
                <MenuItem value="mailgun">Mailgun</MenuItem>
                <MenuItem value="ses">Amazon SES</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* SMTP Host */}
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              label="SMTP Host"
              value={formData.host}
              onChange={(e) => handleInputChange("host", e.target.value)}
              error={!!errors.host}
              helperText={errors.host || "e.g., smtp.gmail.com"}
              required
              InputProps={{
                endAdornment: (
                  <Tooltip title="The SMTP server hostname or IP address">
                    <IconButton size="small">
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ),
              }}
            />
          </Grid>

          {/* SMTP Port */}
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Port"
              type="number"
              value={formData.port}
              onChange={(e) =>
                handleInputChange("port", parseInt(e.target.value))
              }
              error={!!errors.port}
              helperText={errors.port || "587, 465, 25"}
              required
              InputProps={{
                inputProps: { min: 1, max: 65535 },
              }}
            />
          </Grid>

          {/* TLS/SSL Toggle */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.secure}
                  onChange={(e) =>
                    handleInputChange("secure", e.target.checked)
                  }
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <span>Use TLS/SSL</span>
                  <Tooltip title="Enable secure connection to the SMTP server">
                    <InfoIcon fontSize="small" color="action" />
                  </Tooltip>
                </Box>
              }
            />
          </Grid>

          {/* Username */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Username"
              value={formData.username}
              onChange={(e) => handleInputChange("username", e.target.value)}
              error={!!errors.username}
              helperText={errors.username || "SMTP authentication username"}
              required
            />
          </Grid>

          {/* Password */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={(e) => handleInputChange("password", e.target.value)}
              error={!!errors.password}
              helperText={
                errors.password ||
                (configLoaded
                  ? "Leave blank to keep current password"
                  : "SMTP authentication password")
              }
              required={!configLoaded}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => {
                      console.log(
                        "👁️ Password visibility toggled:",
                        !showPassword
                      );
                      setShowPassword(!showPassword);
                    }}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />
          </Grid>

          {/* From Address */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="From Email Address"
              type="email"
              value={formData.fromAddress}
              onChange={(e) => handleInputChange("fromAddress", e.target.value)}
              error={!!errors.fromAddress}
              helperText={errors.fromAddress || "Default sender email"}
              required
            />
          </Grid>

          {/* From Name */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="From Name"
              value={formData.fromName}
              onChange={(e) => handleInputChange("fromName", e.target.value)}
              helperText="Optional display name"
            />
          </Grid>

          {/* Test Recipient */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Test Recipient Email"
              type="email"
              value={formData.testRecipient}
              onChange={(e) =>
                handleInputChange("testRecipient", e.target.value)
              }
              error={!!errors.testRecipient}
              helperText={
                errors.testRecipient || "Email address to receive test message"
              }
            />
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                startIcon={
                  testLoading ? <CircularProgress size={20} /> : <SendIcon />
                }
                onClick={handleTestConnection}
                disabled={loading || testLoading}
              >
                Test Connection
              </Button>
              <Button
                variant="contained"
                startIcon={
                  loading ? <CircularProgress size={20} /> : <SaveIcon />
                }
                onClick={handleSave}
                disabled={loading || testLoading}
              >
                Save Configuration
              </Button>
            </Box>
          </Grid>
        </Grid>

        {configLoaded && (
          <Alert severity="info" sx={{ mt: 3 }}>
            Configuration loaded. Update any fields as needed and save to apply
            changes.
          </Alert>
        )}
      </Paper>
    </Box>
  );
};

export default EmailSetupPage;
